/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Color from 'color';
import { updateColorSchema } from '../../../../../state/thunks/commenting';
// utils
import { url } from '../../../../../utils/urls';
// material
import { ColorPicker, Icon } from '@insticator/insticator-ui'; // iui-material
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller'
import ExpandableSection from '../../../../../material/expandablesection/expandablesection'
import SectionDescription from '../../../../../material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription'
// svg assets
import { ReactComponent as ThumbDown } from './imgassets/thumb-down-alt.svg'
import { ReactComponent as ThumbUp } from './imgassets//thumb-up-alt.svg'
import { ReactComponent as ThreeDots } from './imgassets/more-horiz.svg'
import { ReactComponent as Smile } from './imgassets/mood.svg'
import { ReactComponent as Comment } from './imgassets/author-comments-count.svg'
// styles
import injectSheet from 'react-jss';
import styles from './colorschemesection_styles'


/* ========== ~~~~~~~~~~ COLOR SCHEME SECTION ~~~~~~~~~~ ========== */
const ColorSchemeSection = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { 
    data: { colorSchema: configColorSchema }, 
    isUpdating 
  } = useSelector(state => state.commenting.commentingConfig)

  const [colorSchema, setColorSchema] = useState({
    ...configColorSchema,
    background: `#${configColorSchema.background}`,
    branding: `#${configColorSchema.branding}`,
    errorValidation: `#${configColorSchema.errorValidation}`,
    primaryButtonLabel: `#${configColorSchema.primaryButtonLabel}`,
    primaryText: `#${configColorSchema.primaryText}`,
    secondaryText: `#${configColorSchema.secondaryText}`
  })
  const [colorSchemaValid, setColorSchemaValid] = useState({
      background: true, branding: true, errorValidation: true, 
      primaryButtonLabel: true, primaryText: true, secondaryText: true
  })
  const [isOpened, setIsOpened] = useState(false);


  const processColorUpdate = (color, name) => {
    setColorSchema({
      ...colorSchema,
      [name]: color
    })
  }

  const validateColor = (isValid, name) => {
    setColorSchemaValid({
      ...colorSchemaValid,
      [name]: isValid
    })
  }

  const handleReset = (name) => {
    setColorSchema({
      ...colorSchema,
      [name]: `#${configColorSchema[name]}`
    })
    setColorSchemaValid({
      ...colorSchemaValid,
      [name]: true
    })
  }

  const colortorgba = (colorstring, opacity) => {
    try {
      const cl = Color(colorstring);
      return cl.alpha(opacity).hsl().string();
    } catch(err) {
      return colorstring;
    }
  }

  const formatHex = hex => `#${hex}`

  const isChanged = useMemo(() => {
    const currentColorSchema = {...colorSchema}
    Object.keys(currentColorSchema).map(color => {
      if(color !== 'uuid') currentColorSchema[color] = currentColorSchema[color].replace('#','')
    })
    const isFieldsChanged = !(JSON.stringify(currentColorSchema) === JSON.stringify(configColorSchema));
    return isFieldsChanged
  }, [colorSchema, configColorSchema])

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async () => {
    const response = await dispatch(updateColorSchema({colorSchemaConfigurationUUID: configColorSchema.uuid, colorSchema}))
    setIsOpened(false)
  })

  const handleCancel = () => {
    setIsOpened(false)
    const initialConfig = {...configColorSchema}
    Object.keys(initialConfig).map(color => {
      if(color !== 'uuid') initialConfig[color] = `#${initialConfig[color]}`
    })
    setColorSchema(initialConfig)
  }

  const controller = (
    <ConfigController
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.colorSchema}
      disableSave={!isChanged}
      title='Color Scheme'
      description={<SectionDescription
        content={
          <div className={'colorspreview'}>
            <span className={`swatch`} style={{ background: formatHex(configColorSchema.background), right: '-15px', zIndex: 1 }} />
            <span className={`swatch`} style={{ background: formatHex(configColorSchema.primaryText), zIndex: 2 }} />
            <span className={`swatch`} style={{ background: formatHex(configColorSchema.secondaryText), left: '-15px', zIndex: 3 }} />
            <span className={`swatch`} style={{ background: formatHex(configColorSchema.branding), left: '-30px', zIndex: 4 }} />
          </div>
        }
      />}
    />
  );

  return <ExpandableSection
    className={`colorschemesection ${props.classes.colorschemesection}`}
    controller={controller}
    isOpened={isOpened}
    handleToggle={handleToggle}
    isProcessing={isUpdating.colorSchema}
    handleCancel={handleCancel}
    handleSave={handleSave}
    disableSave={!isChanged}
  >
    <div className={'colorschemeconfig'}>
      <SectionCard>
        <div className={'content'}>
          <SectionGroup>
            <SectionGroupDescription
              title='Set custom colors'
              description='Customize your color scheme if you want to add personality to the comments.'
            />
            <div className={'colorpickercolumn'}>
              <ColorPicker
                handleReset={() => handleReset('background')}
                valid={colorSchemaValid.background}
                color={colorSchema.background}
                savedColor={formatHex(configColorSchema.background)}
                label={'Background'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'background')} 
                validateColor={(isValid, e) => validateColor(isValid, 'background')} 
              />
              <ColorPicker 
                handleReset={() => handleReset('primaryText')}
                valid={colorSchemaValid.primaryText}
                color={colorSchema.primaryText}
                savedColor={formatHex(configColorSchema.primaryText)}
                label={'Primary Text'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'primaryText')} 
                validateColor={(isValid, e) => validateColor(isValid, 'primaryText')}
              />
              <ColorPicker
                handleReset={() => handleReset('secondaryText')}
                valid={colorSchemaValid.secondaryText}
                color={colorSchema.secondaryText}
                savedColor={formatHex(configColorSchema.secondaryText)}
                label={'Secondary Text'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'secondaryText')} 
                validateColor={(isValid, e) => validateColor(isValid, 'secondaryText')}
              />
              <ColorPicker
                handleReset={() => handleReset('branding')}
                valid={colorSchemaValid.branding}
                color={colorSchema.branding}
                savedColor={formatHex(configColorSchema.branding)}
                label={'Branding'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'branding')} 
                validateColor={(isValid, e) => validateColor(isValid, 'branding')}
              />
            </div>
          </SectionGroup>
          <SectionGroup>
            <SectionGroupDescription
              title='Advanced color configuration'
              description='Configure additional colors if you see any color conflict in the preview mode.'
            />
            <div className={'colorpickercolumn'}>
              <ColorPicker
                handleReset={() => handleReset('primaryButtonLabel')}
                valid={colorSchemaValid.primaryButtonLabel}
                color={colorSchema.primaryButtonLabel}
                savedColor={formatHex(configColorSchema.primaryButtonLabel)}
                label={'Primary Button Labels'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'primaryButtonLabel')} 
                validateColor={(isValid, e) => validateColor(isValid, 'primaryButtonLabel')}
              />
              <ColorPicker
                handleReset={() => handleReset('errorValidation')}
                valid={colorSchemaValid.errorValidation}
                color={colorSchema.errorValidation}
                savedColor={formatHex(configColorSchema.errorValidation)}
                label={'Error Validation'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'errorValidation')} 
                validateColor={(isValid, e) => validateColor(isValid, 'errorValidation')}
              />
            </div>
          </SectionGroup>
        </div>
      </SectionCard>
    </div>

    <div className={'colorschemepreview'}>
      <SectionCard>
        <div className={'previewcontainer'} style={{background: colorSchema.background}}>
          <div className={'previewheader'}>
            <span style={{color: colorSchema.primaryText}}>15 Comments</span>
            <img src={`${url.image}/avatars/avatar-colorscheme-preview-group-2x.png`} />
          </div>
          <div className={'commentfield'} style={{background: colortorgba(colorSchema.primaryText, 0.02), border: `2px solid ${colortorgba(colorSchema.primaryText, 0.05)}`}}>
            <span style={{color: colortorgba(colorSchema.secondaryText, 0.5)}}>Leave a comment</span>
          </div>
          <div className={'navbarcontainer'} style={{color: colorSchema.secondaryText}}>
            <div className={'comments'} style={{color: colorSchema.primaryText}}>Comments</div>
            <div className={'community'}>Community</div>
            <span>Log In</span>
          </div>
          <div className={'navbarunderline'} style={{background: colorSchema.branding}} />
          <div className={'sortbycontainer'} style={{color: colorSchema.secondaryText}}>
            <span>Sort By</span>
            <div className={'popular'}>
              <span>Popular</span>
              <Icon name={'angle-down'} />
            </div>
          </div>
          <div className={'commentsection'}>
            <div className={'userinfo'}>
              <img src={`${url.image}/avatars/avatar-cu-preview-oval-2x.png`} />
              <div className={'nameanddate'}>
                <div className={'name'}>
                  <span style={{color: colorSchema.primaryText}}>Dee Chilu…</span>
                  <div className={'diamond'} style={{background: colorSchema.branding}}></div>
                </div>
                <span style={{color: colorSchema.secondaryText}}>Mar 4</span>
              </div>
              <div className={'promotedbutton'} style={{background: colorSchema.branding}}>
                <div className={'promoted'} style={{color: colorSchema.primaryButtonLabel}}>
                  <span>PROMOTED</span>
                  <Comment style={{stroke: colorSchema.primaryButtonLabel }} />
                </div>
              </div>
            </div>
            <div className={'comment'} style={{color: colorSchema.primaryText}}>
              One of the sweetest, emotional and best animated film of the year.
            </div>
            <div className={'reactionbar'} style={{color: colorSchema.secondaryText}}>
              <ThumbUp style={{stroke: colorSchema.secondaryText, marginRight: '10px' }} />
              <span>7</span>
              <ThumbDown style={{stroke: colorSchema.secondaryText, marginLeft: '10px', marginRight: '20px' }}/>
              <Smile style={{stroke: colorSchema.secondaryText, fill: colorSchema.secondaryText, marginRight: '20px' }} />
              <span>Reply</span>
              <ThreeDots style={{stroke: colorSchema.secondaryText, marginLeft: 'auto'}} />
            </div>
            <div className={'morecommentfield'} style={{background: colortorgba(colorSchema.primaryText,  0.05)}}>
              <span style={{color: colorSchema.branding}}>Load more comments</span>
            </div>
          </div>
        </div>
      </SectionCard>
    </div>
  </ExpandableSection>
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ColorSchemeSection;